/**
 * Centralized Header Template Styles Configuration
 *
 * This file contains all style configurations for the 6 header templates.
 * Each template has its own unique design with specific colors, fonts, icons, and layout.
 *
 * WHY THIS EXISTS:
 * - Single source of truth for all header template styles
 * - Prevents duplication across components
 * - Makes it easy to maintain and update template styles
 *
 * USAGE:
 * import { HEADER_TEMPLATE_STYLES } from 'src/app/shared/header-template-styles';
 * const style = HEADER_TEMPLATE_STYLES['template1'];
 */

export interface HeaderTemplateStyle {
  colorSource: string
  color: string
  hover_color?: string
  header_shadow: string
  icons_bg: string
  menu_icon: string
  search_icon: string
  cart_icon: string
  side_menu: string
  font_family: string
  font_size: string
  font_weight: string
  line_height: string
  icon_size?: string
  search_input_width?: string
  search_input_focus_width?: string
  divider_color?: string
  divider_height?: string
  nav_item_spacing?: string
  icon_spacing?: string
  // Logo styling - consistent across all templates
  logo_height?: string
  logo_width?: string
  logo_max_width?: string
  logo_object_fit?: string
  layout?: {
    type: string
    flexDirection: string
    alignItems: string
    justifyContent: string
    textAlign?: string
    navJustify?: string
    padding?: string
    logoHeight?: string
    navPadding?: string
    navMargin?: string
    hideNavMobile?: boolean
    boxShadow?: string
    borderBottom?: string
    showDividers?: boolean
    compactBgColor?: string
    // Template 6 specific - Sidebar Focus with rounded boxes
    sidebarWidth?: string
    boxBorderRadius?: string
    boxBackgroundColor?: string
    boxPadding?: string
    boxGap?: string
    iconRowDirection?: string
    iconBoxSize?: string
    iconBorderRadius?: string
  }
}

export const HEADER_TEMPLATE_STYLES: { [key: string]: HeaderTemplateStyle } = {
  template1: {
    colorSource: 'globalColors',
    color: '#000000',
    hover_color: '#000000',
    header_shadow: 'withHeaderShadow',
    icons_bg: 'noIconBG',
    menu_icon: 'menu',
    search_icon: 'search',
    cart_icon: 'shopping_bag',
    side_menu: 'more_vert',
    font_family: 'Open Sans, sans-serif',
    font_size: '16px',
    font_weight: '400',
    line_height: '32px',
    icon_size: '20px',
    search_input_width: '120px',
    search_input_focus_width: '150px',
    divider_color: '#000000',
    divider_height: '16px',
    nav_item_spacing: '12px',
    icon_spacing: '8px',
    // Logo styling
    logo_height: '70px',
    logo_width: 'auto',
    logo_max_width: '280px',
    logo_object_fit: 'contain',
    // Layout configuration
    layout: {
      type: 'centered', // Menu & Search Left, Logo Center, Actions Right
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      textAlign: 'center',
      navJustify: 'center',
      padding: '20px',
      showDividers: true
    }
  },
  template2: {
    colorSource: 'globalColors',
    color: '#5D5D5D',
    hover_color: '#000000',
    header_shadow: 'withHeaderShadow',
    icons_bg: 'noIconBG',
    menu_icon: 'menu',
    search_icon: 'search',
    cart_icon: 'shopping_bag',
    side_menu: 'more_vert',
    font_family: 'Open Sans, sans-serif',
    font_size: '16px',
    font_weight: '400',
    line_height: '32px',
    icon_size: '18px',
    search_input_width: '100px',
    search_input_focus_width: '130px',
    divider_color: '#000000',
    divider_height: '15px',
    nav_item_spacing: '10px',
    icon_spacing: '5px',
    // Logo styling
    logo_height: '70px',
    logo_width: 'auto',
    logo_max_width: '280px',
    logo_object_fit: 'contain',
    // Layout configuration
    layout: {
      type: 'classic', // Logo Left, Nav Center, Actions Right
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      navJustify: 'center',
      padding: '15px',
      showDividers: true
    }
  },
  template3: {
    colorSource: 'globalColors',
    color: '#5D5D5D',
    hover_color: '#FFFFFF',
    header_shadow: 'withHeaderShadow',
    icons_bg: 'noIconBG',
    menu_icon: 'menu',
    search_icon: 'search',
    cart_icon: 'shopping_bag',
    side_menu: 'more_vert',
    font_family: 'Open Sans, sans-serif',
    font_size: '16px',
    font_weight: '400',
    line_height: '32px',
    icon_size: '18px',
    search_input_width: '100px',
    search_input_focus_width: '120px',
    divider_color: '#333333',
    divider_height: '14px',
    nav_item_spacing: '8px',
    icon_spacing: '4px',
    // Logo styling
    logo_height: '70px',
    logo_width: 'auto',
    logo_max_width: '280px',
    logo_object_fit: 'contain',
    // Layout configuration
    layout: {
      type: 'compact', // All in one line
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0.5rem 0',
      logoHeight: '70px',
      navPadding: '0.25rem 0.75rem',
      showDividers: false,
      compactBgColor: '#e7e7e7'
    }
  },
  template4: {
    colorSource: 'globalColors',
    color: '#5D5D5D',
    hover_color: '#000000',
    header_shadow: 'withHeaderShadow',
    icons_bg: 'noIconBG',
    menu_icon: 'menu',
    search_icon: 'search',
    cart_icon: 'shopping_bag',
    side_menu: 'more_vert',
    font_family: 'Open Sans, sans-serif',
    font_size: '16px',
    font_weight: '500',
    line_height: '32px',
    icon_size: '18px',
    search_input_width: '110px',
    search_input_focus_width: '140px',
    divider_color: '#000000',
    divider_height: '16px',
    nav_item_spacing: '10px',
    icon_spacing: '6px',
    // Logo styling
    logo_height: '70px',
    logo_width: 'auto',
    logo_max_width: '280px',
    logo_object_fit: 'contain',
    // Layout configuration
    layout: {
      type: 'split', // Search Left, Logo Center, Actions Right (Row 1), Menu Center (Row 2)
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '15px',
      navMargin: '0 auto',
      showDividers: true
    }
  },
  template5: {
    colorSource: 'globalColors',
    color: '#5D5D5D',
    hover_color: '#000000',
    header_shadow: 'noHeaderShadow',
    icons_bg: 'noIconBG',
    menu_icon: 'menu',
    search_icon: 'search',
    cart_icon: 'shopping_bag',
    side_menu: 'more_vert',
    font_family: 'Open Sans, sans-serif',
    font_size: '16px',
    font_weight: '400',
    line_height: '32px',
    icon_size: '22px',
    search_input_width: '100px',
    search_input_focus_width: '130px',
    divider_color: '#000000',
    divider_height: '15px',
    nav_item_spacing: '10px',
    icon_spacing: '5px',
    // Logo styling
    logo_height: '70px',
    logo_width: 'auto',
    logo_max_width: '280px',
    logo_object_fit: 'contain',
    // Layout configuration
    layout: {
      type: 'side-menu', // Hamburger menu style
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      padding: '20px 0',
      hideNavMobile: true,
      showDividers: false
    }
  },
  template6: {
    colorSource: 'globalColors',
    color: '#8A8A8A',
    hover_color: '#000000',
    header_shadow: 'noHeaderShadow',
    icons_bg: 'noIconBG',
    menu_icon: 'menu',
    search_icon: 'search',
    cart_icon: 'shopping_bag',
    side_menu: 'more_horiz',
    font_family: 'Open Sans, sans-serif',
    font_size: '16px',
    font_weight: '400',
    line_height: '32px',
    icon_size: '22px',
    search_input_width: '105px',
    search_input_focus_width: '135px',
    divider_color: '#e0e0e0',
    divider_height: '15px',
    nav_item_spacing: '8px',
    icon_spacing: '12px',
    // Logo styling
    logo_height: '70px',
    logo_width: 'auto',
    logo_max_width: '280px',
    logo_object_fit: 'contain',
    // Layout configuration - Sidebar Focus with rounded boxes
    layout: {
      type: 'sidebar-focus', // Always open sidebar with rounded box sections
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      padding: '20px',
      boxShadow: '2px 0 12px rgba(0, 0, 0, 0.08)',
      borderBottom: 'none',
      showDividers: false,
      // Rounded box styling
      sidebarWidth: '220px',
      boxBorderRadius: '16px',
      boxBackgroundColor: '#FBFBFB',
      boxPadding: '16px',
      boxGap: '16px',
      // Icon row configuration
      iconRowDirection: 'row',
      iconBoxSize: '44px',
      iconBorderRadius: '12px'
    }
  }
}

/**
 * Get template style configuration by template ID
 * @param templateId - The template ID (template1-6)
 * @returns The template's style configuration, defaults to template2 if not found
 */
export function getHeaderTemplateStyle(templateId: string): HeaderTemplateStyle {
  return HEADER_TEMPLATE_STYLES[templateId] || HEADER_TEMPLATE_STYLES['template2']
}
